import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RefreshCw, BookOpen } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const balanceClass = (n) => {
  if (n > 0) return "text-[hsl(6_62%_45%)] font-semibold";
  if (n < 0) return "text-[hsl(var(--forest))]";
  return "text-[hsl(var(--ink-soft))]";
};

export default function ReceivablesTab({ onViewLedger }) {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [showZero, setShowZero] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/staff/product-out/receivables");
      setData(res.data);
    } catch { toast.error("Could not load receivables"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = showZero ? data.rows : (data.rows || []).filter((r) => r.balance_due !== 0 || r.dispatch_count > 0);
  const s = data.summary || {};

  return (
    <div data-testid="receivables-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showZero} onChange={(e) => setShowZero(e.target.checked)} data-testid="receivables-show-zero" />
          Show buyers with no history
        </label>
        <button onClick={load} className="inline-flex items-center gap-2 text-sm px-3 py-2 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg-2))]">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile testid="rcv-buyers" label="Buyers" value={s.buyers_count || 0} />
        <SummaryTile testid="rcv-with-balance" label="With outstanding balance" value={s.with_balance || 0} accent />
        <SummaryTile testid="rcv-total-dispatched" label="Total dispatched" value={inr(s.total_dispatched)} />
        <SummaryTile testid="rcv-outstanding" label="Outstanding" value={inr(s.outstanding)} highlight={balanceClass(s.outstanding || 0)} />
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-left text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))] sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="px-4 py-2">Buyer</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2 text-right">Dispatches</th>
              <th className="px-4 py-2">Last dispatch</th>
              <th className="px-4 py-2 text-right">Dispatched</th>
              <th className="px-4 py-2 text-right">Received</th>
              <th className="px-4 py-2 text-right">Balance</th>
              <th className="px-4 py-2 text-right">Ledger</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-[hsl(var(--ink-soft))]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-[hsl(var(--ink-soft))]">No buyer activity yet.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.buyer_id} data-testid={`receivable-row-${r.buyer_id}`} className="border-t border-[hsl(var(--rule))]">
                <td className="px-4 py-2 font-semibold">{r.buyer_name}</td>
                <td className="px-4 py-2 text-[hsl(var(--ink-soft))]">{r.category || "—"}</td>
                <td className="px-4 py-2 text-right">{r.dispatch_count}</td>
                <td className="px-4 py-2">{r.last_dispatch_date || "—"}</td>
                <td className="px-4 py-2 text-right">{inr(r.total_dispatched)}</td>
                <td className="px-4 py-2 text-right">{inr(r.total_received)}</td>
                <td className={`px-4 py-2 text-right ${balanceClass(r.balance_due)}`}>{inr(r.balance_due)}</td>
                <td className="px-4 py-2 text-right">
                  <button data-testid={`receivable-ledger-${r.buyer_id}`} onClick={() => onViewLedger?.(r.buyer_id)} className="inline-flex items-center gap-1 text-[hsl(var(--forest))] hover:underline">
                    <BookOpen size={12} /> Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, testid, accent, highlight }) {
  return (
    <div data-testid={testid} className={`border ${accent ? "border-[hsl(var(--forest))]" : "border-[hsl(var(--rule))]"} bg-[hsl(var(--bg))] p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`display text-2xl mt-1 ${highlight || ""}`}>{value}</div>
    </div>
  );
}
